import React, { useEffect, useMemo, useState } from "react";
import "./ManageUpcomingTradeSetupPlan.css";
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";



  const BASE_SERVICE_URL =
  "/api/bot/ui/get/upcoming-plan";

const ManageUpcomingTradeSetupPlan = () => {
  const { workspace, exchange, marketType } = useWorkspace();
  const [records, setRecords] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editRowId, setEditRowId] = useState(null);
  const [formData, setFormData] = useState({});
  const [newRecord, setNewRecord] = useState({});
  const [loading, setLoading] = useState(true);

  const scopedWorkspaceLabel = useMemo(
    () => `${exchange} / ${marketType}`,
    [exchange, marketType]
  );

  useEffect(() => {
    fetchAll();
  }, [exchange, marketType]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/getAll`, workspace),
                      {
                        headers: getApiHeaders()
                      });
      const data = await res.json();
      const normalizedRecords = ensureArray(data);
      if (!Array.isArray(data)) {
        console.warn("Unexpected upcoming trade setup payload shape", data);
      }
      setRecords(normalizedRecords);
      setFiltered(normalizedRecords);
    } catch (err) {
      console.error("Fetch error:", err);
      setRecords([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  const applyFilters = (f) => {
    let result = [...records];
    Object.keys(f).forEach((key) => {
      const val = String(f[key] || "").toLowerCase().trim();
      if (val)
        result = result.filter((r) =>
          String(r[key] || "").toLowerCase().includes(val)
        );
    });
    setFiltered(result);
  };

  const clearAllFilters = () => {
    setFilters({});
    setFiltered(records);
  };

  const handleSelectRow = (id) => {
    const newSet = new Set(selectedIds);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedIds(newSet);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return alert("No rows selected!");
    if (!window.confirm("Delete selected records?")) return;
    for (let id of selectedIds) {
      await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/delete/${id}`, workspace), { method: "DELETE", headers: getApiHeaders() });
    }
    fetchAll();
    setSelectedIds(new Set());
  };

  const handleDeleteFiltered = async () => {
    if (filtered.length === 0) return alert("No records to delete!");
    if (!window.confirm("Delete all filtered records?")) return;
    for (let rec of filtered) {
      await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/delete/${rec.id}`, workspace), { method: "DELETE", headers: getApiHeaders() });
    }
    fetchAll();
  };

  const handleEdit = (row) => {
    setEditRowId(row.id);
    setFormData({ ...row });
  };

  const handleSave = async () => {
    await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/save`, workspace), {
      method: "POST",
      headers: getApiHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ ...formData, exchange, marketType }),
    });
    fetchAll();
    setEditRowId(null);
  };

  const handleAddNew = async () => {
    await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/save`, workspace), {
      method: "POST",
      headers: getApiHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ ...newRecord, exchange, marketType }),
    });
    setNewRecord({});
    fetchAll();
  };

  const handleCancel = () => {
    setEditRowId(null);
    setFormData({});
  };

  const handleInputChange = (key, value) =>
    setFormData({ ...formData, [key]: value });

  if (loading) return <div className="uts-loading">Loading setup plans...</div>;

  const columns = [
    "symbol",
    "timeFrame",
    "focusPriorityLevel",
    "confluences",
    "watchingNext",
    "conditionToSatisfy",
    "conditionSatisfiedOn",
    "tradeSetupConsidered",
    "possibleTPLevel",
    "possibleSLLevel",
    "possibleEntryLevel",
    "comments",
    "journaling",
    "invalidated",
    "invalidatedBy",
    "invalidatedOn",
    "alertSent",
    "status",
    "createdDate",
    "modifiedDate",
  ];

  return (
    <div className="uts-modern-container">
      <div className="uts-header">
        <h2 className="page-title">📊 Upcoming Trade Setup Plan</h2>
        <div className="workspace-subtitle">Workspace: {scopedWorkspaceLabel}</div>
        <div className="uts-actions">
          <button onClick={fetchAll}>Refresh</button>
          <button onClick={clearAllFilters}>Clear Filters</button>
          <button onClick={handleDeleteSelected}>Delete Selected</button>
          <button onClick={handleDeleteFiltered}>Delete Filtered</button>
        </div>
      </div>

      {/* Add new */}
      <div className="uts-card">
        <div className="uts-card-header">Add New Record</div>
        <div className="uts-form-grid">
          {["symbol", "timeFrame", "focusPriorityLevel", "confluences", "watchingNext"].map(
            (f) => (
              <input
                key={f}
                placeholder={f}
                value={newRecord[f] || ""}
                onChange={(e) =>
                  setNewRecord({ ...newRecord, [f]: e.target.value })
                }
              />
            )
          )}
          <button onClick={handleAddNew} className="uts-add-btn">
            ➕ Add
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="uts-filters">
        {columns.map((key) => (
          <input
            key={key}
            placeholder={`Filter ${key}`}
            value={filters[key] || ""}
            onChange={(e) => handleFilterChange(key, e.target.value)}
          />
        ))}
      </div>

      {/* Table */}
      <div className="uts-table-card">
        <table className="uts-table">
          <thead>
            <tr>
              <th>Select</th>
              {columns.map((c) => (
                <th key={c}>{c}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((row) => (
              <tr
                key={row.id}
                className={
                  selectedIds.has(row.id) ? "uts-row-selected" : undefined
                }
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(row.id)}
                    onChange={() => handleSelectRow(row.id)}
                  />
                </td>
                {columns.map((key) => {
                  const isEditing = editRowId === row.id;
                  return (
                    <td key={key}>
                      {isEditing ? (
                        <input
                          value={formData[key] ?? ""}
                          onChange={(e) =>
                            handleInputChange(key, e.target.value)
                          }
                        />
                      ) : (
                        String(row[key] ?? "")
                      )}
                    </td>
                  );
                })}
                <td>
                  {editRowId === row.id ? (
                    <>
                      <button className="save" onClick={handleSave}>
                        💾
                      </button>
                      <button className="cancel" onClick={handleCancel}>
                        ✖
                      </button>
                    </>
                  ) : (
                    <button className="edit" onClick={() => handleEdit(row)}>
                      ✎
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageUpcomingTradeSetupPlan;
